import type { IntlShape } from 'react-intl'
import { min } from 'lib/array/arrayUtils'
import { getKilometersDistanceFromLatLong } from 'lib/flights/getDistanceFromLatLong'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'

export interface TransferForm {
  toAirport: TransferFormTransfer
  toHotel: TransferFormTransfer
}

export interface SingleTransferForm {
  transfer: TransferFormTransfer
}

export interface TransferFormTransfer {
  type: App.ExperienceTransferType
  time: string
  date: string
  flightNumber: string
  travellers: any
}

export function formatTransferTravellers(
  travellers: App.ExperienceTransferTravellers,
  intl?: IntlShape,
) {
  let travellerText = formatOccupantsShort({ adults: travellers.adults, children: travellers.children })
  if (travellers.children) {
    const childSeats = travellers.childSeats ?? 0
    const boosterSeats = travellers.boosterSeats ?? 0
    if (travellers.children > 0 && (childSeats > 0 || boosterSeats > 0)) {
      const seats = [
        childSeats > 0 ? intl?.formatMessage(
          { id: 'bt1S11s3T6', defaultMessage: '{count, plural, one {# child seat} other {# child seats}}', description: 'Child seat count in transfer traveller summary in the experiences section' },
          { count: childSeats },
        ) ?? `${childSeats} child seat${childSeats !== 1 ? 's' : ''}` : undefined,
        boosterSeats > 0 ? intl?.formatMessage(
          { id: '5w1bU1ekbm', defaultMessage: '{count, plural, one {# booster seat} other {# booster seats}}', description: 'Booster seat count in transfer traveller summary in the experiences section' },
          { count: boosterSeats },
        ) ?? `${boosterSeats} booster seat${boosterSeats !== 1 ? 's' : ''}` : undefined,
      ].filter(Boolean)
      travellerText += ` (${seats.join(', ')})`
    }
  }
  return travellerText
}

export function formatTransferBaggage(
  baggage: App.OrderTransferItemBaggage,
  intl?: IntlShape,
) {
  const bagsText = intl?.formatMessage(
    { id: 'wOY4k7IQjE', defaultMessage: '{count, plural, one {# bag} other {# bags}}', description: 'Bag count in transfer baggage summary in the airport transfer booking section' },
    { count: baggage.bags },
  ) ?? `${baggage.bags} bag${baggage.bags !== 1 ? 's' : ''}`
  const oversizedText = (baggage.oversizedBags ?? 0) > 0
    ? ` (${intl?.formatMessage(
        { id: 'xfUeo/I2Fj', defaultMessage: '{count, plural, one {# oversized bag} other {# oversized bags}}', description: 'Oversized bag count in transfer baggage summary in the experiences section' },
        { count: baggage.oversizedBags },
      ) ?? `${baggage.oversizedBags} oversized bag${baggage.oversizedBags !== 1 ? 's' : ''}`})`
    : ''
  return [bagsText, oversizedText].filter(Boolean).join('')
}

export function getValidTransferOptions(
  view: App.ExperienceTransferView,
  transfers: Array<App.ExperienceTransferOption>,
) {
  const { hotel, travellers, baggage } = view
  const childCount = travellers.children ?? 0
  const passengers = travellers.adults + childCount
  const childSeats = childCount > 0 ? travellers.childSeats ?? 0 : 0
  const boosterSeats = childCount > 0 ? travellers.boosterSeats ?? 0 : 0
  const hotelDistance = hotel.kmFromAirport ?? 0
  return transfers.filter(transfer => {
    return transfer.maxPassengers >= passengers &&
      transfer.maxTransferDistance >= hotelDistance &&
      (childSeats === 0 || transfer.hasChildSeats) &&
      (boosterSeats === 0 || transfer.hasBoosterSeats) &&
      (baggage && (baggage.bags === 0 || transfer.maxCheckedBags >= baggage.bags))
  })
}

export type NoTransferReason = 'distance' | 'passengers' | 'childSeats' | 'bags'
export function findBestTransfer(
  view: App.ExperienceTransferView,
  transfers: Array<App.ExperienceTransferOption>,
): {
  bestTransfer?: App.ExperienceTransferOption
  bestLuxPlusTransfer?: App.ExperienceTransferOption
  noTransferReason?: NoTransferReason
} {
  const { hotel, travellers, baggage } = view
  if (hotel.kmFromAirport) {
    const passengers = travellers.adults + (travellers.children ?? 0)
    const validTransfers = getValidTransferOptions(view, transfers)

    if (validTransfers.length === 0) {
      // now work out why it's not valid, proritise distance first as it's the most important
      let reason: NoTransferReason
      if (transfers.some(transfer => hotel.kmFromAirport && transfer.maxTransferDistance >= hotel.kmFromAirport)) {
        // too far away
        reason = 'distance'
      } else if (transfers.some(transfer => transfer.maxPassengers >= passengers)) {
        // must have too many passengers
        reason = 'passengers'
      } else if (baggage && baggage.bags > 0 && transfers.some(transfer => transfer.maxCheckedBags >= baggage.bags)) {
        // must have too many bags
        reason = 'bags'
      } else {
        // only reason left
        reason = 'childSeats'
      }

      return {
        bestTransfer: undefined,
        bestLuxPlusTransfer: undefined,
        noTransferReason: reason,
      }
    }

    return {
      bestTransfer: min(validTransfers, transfer => transfer.price),
      bestLuxPlusTransfer: min(validTransfers, transfer => transfer.memberPrice),
    }
  }

  return {}
}

export function getDistanceBetweenPropertyAndAirport(property?: App.OfferProperty | App.BedbankProperty, airport?: App.Airport) {
  if (property && airport) {
    return getKilometersDistanceFromLatLong(
      property.latitude,
      property.longitude,
      airport.latitude,
      airport.longitude,
    )
  }

  return undefined
}
